import React, { useState } from 'react';
import { 
  Folder, 
  FileText, 
  MoreVertical, 
  Clock, 
  User, 
  Calendar,
  ArrowRight,
  ExternalLink,
  Edit,
  FolderInput,
  Trash2,
  File
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { useNavigate } from 'react-router-dom';

const FileIcon = ({ isFolder, className }) => {
  if (isFolder) {
    return <Folder className={cn("text-yellow-500 fill-yellow-500/20", className)} />;
  }
  return <FileText className={cn("text-indigo-500 fill-indigo-500/10", className)} />;
};

const FileActionMenu = ({ file, onOpen, onRename, onMove, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <Button 
        variant="ghost" 
        size="icon" 
        className="h-8 w-8 hover:bg-gray-100 rounded-full"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        <MoreVertical className="h-4 w-4 text-gray-500" />
      </Button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }} 
          />
          <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-100 z-50 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-right">
            <button
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(file);
                setIsOpen(false);
              }}
            >
              <ExternalLink className="h-4 w-4 text-gray-400" /> Open
            </button>
            <button
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onRename(file);
                setIsOpen(false);
              }}
            >
              <Edit className="h-4 w-4 text-gray-400" /> Rename
            </button>
            {!file.isFolder && (
              <button
                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onMove?.(file);
                  setIsOpen(false);
                }}
              >
                <FolderInput className="h-4 w-4 text-gray-400" /> Move
              </button>
            )}
            <div className="h-px bg-gray-100 my-1" />
            <button
              className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(file);
                setIsOpen(false);
              }}
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const FileListView = ({ 
  files = [], 
  viewMode = 'grid', 
  onFileClick, 
  onFileRename, 
  onFileMove,
  onFileDelete,
  isLoading = false,
  userColumnLabel = "Forwarded By"
}) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
        <div className="h-16 w-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
          <Folder className="h-8 w-8 text-indigo-300" />
        </div>
        <h3 className="text-lg font-medium text-gray-900">No files found</h3>
        <p className="text-gray-500 mt-1">This folder is empty</p>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50/80 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500 font-medium">
              <th className="px-6 py-4 w-1/3">Name</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Modified</th>
              <th className="px-6 py-4">{userColumnLabel}</th>
              <th className="px-6 py-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {files.map((file) => (
              <tr 
                key={file.id} 
                onClick={() => onFileClick(file)}
                className="group hover:bg-indigo-50/30 transition-colors cursor-pointer"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-white group-hover:shadow-sm transition-all border border-gray-100">
                      <FileIcon isFolder={file.isFolder} className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-1">
                        {file.subject || file.name}
                        {file.delegatedFrom && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800 ml-2 border border-purple-200 align-middle">
                            Acting for: {file.delegatedFrom}
                          </span>
                        )}
                      </p>
                      {!file.isFolder && (
                        <p className="text-xs text-gray-500 font-mono mt-0.5">{file.fileNumber}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-medium border",
                    file.status === 'Active' ? "bg-green-50 text-green-700 border-green-200" :
                    file.status === 'Closed' ? "bg-gray-100 text-gray-700 border-gray-200" :
                    "bg-yellow-50 text-yellow-700 border-yellow-200"
                  )}>
                    {file.status || 'Active'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col text-xs text-gray-500">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-3 w-3" />
                      {new Date(file.updatedAt).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1.5 mt-0.5">
                      <Clock className="h-3 w-3" />
                      {new Date(file.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                   {(() => {
                      const name = file.fromUserName || file.currentDeskName;
                      const designation = file.fromUserDesignation || file.currentDeskDesignation;
                      
                      if (name) {
                           return (
                               <div className="flex items-center gap-2">
                                 <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600 shrink-0">
                                   {name.charAt(0)}
                                 </div>
                                 <div className="min-w-0">
                                   <p className="text-sm font-medium text-gray-900 truncate" title={name}>
                                     {name}
                                   </p>
                                   {designation && (
                                     <p className="text-xs text-gray-500 truncate" title={designation}>
                                       {designation}
                                     </p>
                                   )}
                                 </div>
                               </div>
                           );
                      }
                      return <span className="text-xs text-gray-400">-</span>;
                   })()}
                </td>
                <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <FileActionMenu 
                    file={file} 
                    onOpen={onFileClick} 
                    onRename={onFileRename} 
                    onMove={onFileMove}
                    onDelete={onFileDelete} 
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Grid View
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {files.map((file) => (
        <div 
          key={file.id}
          onClick={() => onFileClick(file)}
          className="group relative bg-white rounded-xl border border-gray-200 p-4 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer flex flex-col h-full"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="p-2.5 bg-indigo-50/50 rounded-xl group-hover:bg-indigo-100/50 transition-colors">
              <FileIcon isFolder={file.isFolder} className="h-6 w-6" />
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              <FileActionMenu 
                file={file} 
                onOpen={onFileClick} 
                onRename={onFileRename} 
                onMove={onFileMove}
                onDelete={onFileDelete} 
              />
            </div>
          </div>
          
          <div className="flex-1 min-w-0 mb-3">
            {file.delegatedFrom && (
              <div className="mb-1">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-200 truncate max-w-full">
                  Acting for: {file.delegatedFrom}
                </span>
              </div>
            )}
            <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-indigo-600 transition-colors">
              {file.subject || file.name}
            </h3>
            {!file.isFolder && (
              <p className="text-xs text-gray-500 font-mono truncate bg-gray-50 px-1.5 py-0.5 rounded inline-block">
                {file.fileNumber}
              </p>
            )}
          </div>
          
          <div className="flex items-center justify-between text-xs text-gray-400 border-t border-gray-50 pt-3 mt-auto">
            <div className="flex items-center gap-2 min-w-0 mr-2">
               {(() => {
                  const name = file.fromUserName || file.currentDeskName;
                  if (name) {
                      return (
                          <>
                              <div className="h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-600 shrink-0">
                                  {name.charAt(0)}
                              </div>
                              <span className="truncate" title={name}>{name}</span>
                          </>
                      );
                  }
                  return null;
               })()}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(file.updatedAt).toLocaleDateString()}
              </span>
              {file.status && (
                <span className={cn(
                  "h-2 w-2 rounded-full",
                  file.status === 'Active' ? "bg-green-500" : "bg-gray-300"
                )} title={file.status} />
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FileListView;
